/**
 * The `F` vacuum's decision, without the socket.
 *
 * `autoPickupTargets` already answers "which drops does the filter want swept up". This
 * adds the two things a keypress needs and a pure selector must not know about: **how
 * often** a sweep may happen, and **how many** ids one sweep may ask for.
 *
 * ## Why a vacuum needs a throttle at all
 *
 * A filter is presentation only, and the sharpest way to break that is to make the
 * vacuum a faster way to empty a room than a human could click. It issues exactly the
 * same `CMD_PICKUP` a click does — no new command, no server-side change, the server
 * still re-checks existence, liveness, range and capacity for every one — so the only
 * thing left to get wrong is the *rate*.
 *
 * Two limits, and they do different jobs:
 *
 * - **`VACUUM_INTERVAL_MS` bounds the gesture rate.** `F` is a held key, and a held key
 *   repeats at the operating system's rate — around 30 Hz — which would turn one finger
 *   into thirty sweeps a second. A player clicking loot manages perhaps eight actions a
 *   second at the very outside, and 300 ms is comfortably inside that.
 * - **`VACUUM_BATCH` bounds one sweep.** A sweep is a single gesture asking for
 *   everything inside a circle, so it is legitimate for it to want several items — the
 *   same several a player would have clicked. What it must not do is hand the loop an
 *   unbounded burst: back-pressure on the command queue is the loop's own guarantee, and
 *   a batch large enough to be dropped outright would turn "the filter took the good
 *   drops" into "the filter took some of them, silently". Bounding it here means the
 *   queue is never the thing that decides.
 *
 * Pure: the caller supplies `now` and the timestamp of the last accepted sweep, so the
 * whole throttle is testable without a clock.
 */

import type { GroundItem } from '../../net/protocol';
import { autoPickupTargets, VACUUM_RADIUS, type FilterContext, type Point2 } from './evaluate';
import type { CompiledFilter } from './parse';

/**
 * Shortest gap between two accepted sweeps, in milliseconds.
 *
 * **Chosen**, against the rate a human can click rather than against a server limit —
 * there is no server-side pickup cooldown, which is precisely why the client must not
 * behave as though it had one to lean on.
 */
export const VACUUM_INTERVAL_MS = 300;

/**
 * Most ids one sweep may ask for.
 *
 * Eight is more than a 5-unit circle normally holds, so in ordinary play this never
 * binds — it exists for the boss-drop pile the layout solver's `maxLabels` exists for.
 */
export const VACUUM_BATCH = 8;

/** Why a sweep asked for nothing. Null when it asked for something. */
export type VacuumRefusal =
  /** The previous sweep was too recent — a held or mashed key. */
  | 'THROTTLED'
  /** Nothing in range that the filter wants. Includes "no filter loaded". */
  | 'NOTHING_TO_TAKE';

/** What one keypress should do. */
export interface VacuumPlan {
  /** Loot ids to send `CMD_PICKUP` for, nearest first. Empty when refused. */
  ids: string[];
  /** Set when `ids` is empty, so a caller can explain the silence. */
  refusal: VacuumRefusal | null;
  /**
   * How many the filter wanted before the batch cap, so the caller can say "8 of 14".
   * Equal to `ids.length` in every ordinary case.
   */
  wanted: number;
}

export interface VacuumInput {
  ground: readonly GroundItem[];
  filter: CompiledFilter;
  /** Where the player is standing. */
  origin: Point2;
  /** Now, in milliseconds on the same clock as `lastAt`. */
  now: number;
  /** When the last accepted sweep happened, or null if there has not been one. */
  lastAt: number | null;
  radius?: number;
  context?: FilterContext;
  batch?: number;
  intervalMs?: number;
}

/**
 * What a vacuum keypress should ask for.
 *
 * Returns ids and nothing else, exactly as `autoPickupTargets` does — sending is the
 * caller's job, through the same path a click uses.
 */
export function planVacuum(input: VacuumInput): VacuumPlan {
  const interval = input.intervalMs ?? VACUUM_INTERVAL_MS;
  const batch = input.batch ?? VACUUM_BATCH;

  // The throttle is checked first and deliberately: a refused press must cost nothing,
  // and evaluating the whole floor for a keypress that is going to be dropped is work
  // for no answer.
  if (input.lastAt !== null && input.now - input.lastAt < interval) {
    return { ids: [], refusal: 'THROTTLED', wanted: 0 };
  }

  const wanted = autoPickupTargets(input.ground, input.filter, input.origin, {
    radius: input.radius ?? VACUUM_RADIUS,
    context: input.context,
  });

  if (wanted.length === 0) {
    return { ids: [], refusal: 'NOTHING_TO_TAKE', wanted: 0 };
  }

  // Nearest first is already `autoPickupTargets`'s order, so a capped sweep keeps the
  // closest drops — the ones the player is standing on, and the ones the server's own
  // 2.6-unit pickup range will actually grant.
  return { ids: wanted.slice(0, batch), refusal: null, wanted: wanted.length };
}

/**
 * How many drops a vacuum press would take right now, for a HUD affordance.
 *
 * Ignores the throttle on purpose: the hint answers "is there anything here worth
 * pressing F for", and blinking off for 300 ms after every press would make it read as
 * though the loot had gone when the server had not answered yet.
 */
export function vacuumCandidateCount(
  ground: readonly GroundItem[],
  filter: CompiledFilter,
  origin: Point2,
  options: { radius?: number; context?: FilterContext } = {},
): number {
  return autoPickupTargets(ground, filter, origin, {
    radius: options.radius ?? VACUUM_RADIUS,
    context: options.context,
  }).length;
}
